import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';


import { EntitiesService } from '@apps/auth/domain';
import * as fromEntitiesGlobalStoreActions from '@apps/back/store/auth/entities/global/entities-global.actions';
import { Site } from '@apps/priv/models/site';
import { Entity, User } from '@apps/auth/domain/models/back';
import { IAuroraState } from '@apps/aurora.state';
import { BackEntityDetailsSelectSet } from '@apps/back/store/misc/misc.actions';
import { HttpEvent } from '@angular/common/http';
import { Profile } from '@apps/auth/models';
import { FilesService, MediaTypes, NotfGravity, NotifType, NotificationService } from '@common/services';
import * as fromAuthStore from '@apps/auth/store';
import { BackAuthEntityExternalUpdated } from '../external/entities-external.actions';
import { BackAuthEntityInternalUpdated } from '../internal/entities-internal.actions';
import { BackItsmDepartmentRequested } from '../../../itsm/itsm.actions';
import { EntityQueryDefinitionsDomainService } from '@apps/auth/domain/entity-query-definitions-domain.service';
import { EntityMaster } from '@apps/auth/domain/models/back/entity-backup-definition';
import { EntityComputeDefinition } from '@apps/auth/domain/models/back/entity-compute-definition';
import { getSelectedEntity } from './entities-global.selectors';
import { BackAuthEntityUsersRequested } from '@apps/back/store/auth/entities/global/entities-global.actions';
import { getUsersExternalQueryString } from '../../users/external/users-external.selectors';

@Injectable()
export class BackAuthEntitiesGlobalEffects
{
    constructor (
        private actions: Actions,
        private entitiesService: EntitiesService,
        private store: Store<IAuroraState>,
        private filesService: FilesService,
        private notificationService: NotificationService,
        private queryDefinitionsService: EntityQueryDefinitionsDomainService
    )
    { }

    backAuthEntityDetailsRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityDetailsRequested),
                    switchMap(({ entityId }: { entityId: string; }) =>
                        combineLatest([
                            this.entitiesService.get(entityId),
                            this.entitiesService.getUsers(entityId),
                            this.entitiesService.getSites(entityId),
                            this.entitiesService.getReferents(entityId)
                        ])
                            .pipe(
                                tap(([entity ,users, sites, referents]: [entity: Entity, users: User[], sites: Site[],referents: User[]]) =>
                                {
                                    this.store.dispatch(fromEntitiesGlobalStoreActions.BackAuthEntityUsersSucceeded({ users }));
                                    this.store.dispatch(fromEntitiesGlobalStoreActions.BackAuthEntitySitesSucceeded({ sites }));
                                    this.store.dispatch(fromEntitiesGlobalStoreActions.BackAuthEntityReferentsSucceeded({ referents }));
                                    this.store.dispatch(BackEntityDetailsSelectSet({ entity }));
                                    this.store.dispatch(BackItsmDepartmentRequested({ departmentCode: entity.entityShortId }));
                                }),
                                map(([entity,,,]: [Entity, User[], Site[], User[]]) =>
                                    fromEntitiesGlobalStoreActions.BackAuthEntityDetailsSucceeded({
                                        entity
                                    })
                                ),
                                catchError((err: any) => of(fromEntitiesGlobalStoreActions.BackAuthEntityDetailsFailed(err)))
                            )
                    )
                )
    );

    backAuthEntityUsersRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUsersRequested),
                switchMap(({entityId}) =>
                    this.entitiesService.getUsers(entityId)
                        .pipe(
                            map((users: User[]) =>
                                fromEntitiesGlobalStoreActions.BackAuthEntityUsersSucceeded({ users })
                            )
                        )
                )
            )
    );

    backAuthEntityExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityExportRequested),
                    switchMap(({ qs, format }: {qs: string, format: MediaTypes}) =>
                        this.entitiesService.export(qs, format)
                            .pipe(
                                map((response) =>
                                    fromEntitiesGlobalStoreActions.BackAuthEntityExportSucceeded({ response })
                                ),
                                catchError((failure: any) =>
                                    of(fromEntitiesGlobalStoreActions.BackAuthEntityExportFailed(failure))
                                ))
                    )
                )
    );

    backAuthEntityExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob>; }, Profile]) =>
                    {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );

    backAuthEntityUsersExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUsersExportRequested),
                    switchMap(({ entityId, format }: {entityId: string, format: MediaTypes}) =>
                        this.entitiesService.exportEntityUsers(entityId, format)
                            .pipe(
                                map((response) =>
                                    fromEntitiesGlobalStoreActions.BackAuthEntityUsersExportSucceeded({ response })
                                ),
                                catchError((failure: any) =>
                                    of(fromEntitiesGlobalStoreActions.BackAuthEntityUsersExportFailed(failure))
                                ))
                    )
                )
    );

    backAuthEntityUsersExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUsersExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob>; }, Profile]) =>
                    {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );

    backAuthEntityAddHabilitationRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityAddHabilitationRequested),
                switchMap(({ external, entityId, appRole }) =>
                    this.entitiesService.addHabilitations(entityId, [appRole])
                        .pipe(
                            switchMap((entity: Entity) =>
                            {
                                const updateEntity: Update<Entity> = {
                                    id: entity.id,
                                    changes: {
                                        applications: entity.applications
                                    }
                                };

                                return [
                                    fromEntitiesGlobalStoreActions.BackAuthEntityAddHabilitationSucceeded({ appRole }),
                                    external ? BackAuthEntityExternalUpdated({ entity: updateEntity }) : BackAuthEntityInternalUpdated({ entity: updateEntity })
                                ];
                            }),
                            catchError(() =>
                                of(fromEntitiesGlobalStoreActions.BackAuthEntityAddHabilitationFailed)
                            )
                        )
                )
            )
    );

    backAuthEntityAddReferentRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityAddReferentRequested),
                switchMap(({ entityId, userId }) =>
                    this.entitiesService.addReferent(entityId, userId)
                        .pipe(
                            switchMap(() => this.entitiesService.getReferents(entityId)),
                            switchMap((referents: User[]) =>
                                of(
                                    fromEntitiesGlobalStoreActions.BackAuthEntityReferentsSucceeded({ referents }),
                                    fromEntitiesGlobalStoreActions.BackAuthEntityAddReferentSucceeded({ referents })
                                )
                            ),
                            catchError((err: any) =>
                                of(fromEntitiesGlobalStoreActions.BackAuthEntityAddReferentFailed({ err }))
                            )
                        )
                )
            )
    );

    backAuthEntityRemoveReferentRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveReferentRequested),
                switchMap(({ entityId, userId }) =>
                    this.entitiesService.removeReferent(entityId, userId)
                        .pipe(
                            switchMap(() => this.entitiesService.getReferents(entityId)),
                            switchMap((referents: User[]) =>
                                of(
                                    fromEntitiesGlobalStoreActions.BackAuthEntityReferentsSucceeded({ referents }),
                                    fromEntitiesGlobalStoreActions.BackAuthEntityRemoveReferentSucceeded({ referents })
                                )
                            ),
                            catchError((err: any) =>
                                of(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveReferentFailed({ err }))
                            )
                        )
                )
            )
    );

    backAuthEntityAddHabilitationSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityAddHabilitationSucceeded),
                tap(({ appRole }) =>
                {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.ENTITIES.ROLES.SUCCESS.ADDED'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityAddHabilitationFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityAddHabilitationFailed),
                tap(({ appRole }) =>
                {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.ENTITIES.ROLES.FAILED.ADDED'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityAddReferentSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityAddReferentSucceeded),
                tap(() =>
                {
                    this.notificationService.notify(
                        [
                            'BACK.ENTITIES.REFERENT.ADD.TITLE',
                            'BACK.ENTITIES.REFERENT.ADD.SUCCEEDED'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityRemoveReferentSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveReferentSucceeded),
                tap(() =>
                {
                    this.notificationService.notify(
                        [
                            'BACK.ENTITIES.REFERENT.REMOVE.TITLE',
                            'BACK.ENTITIES.REFERENT.REMOVE.SUCCEEDED'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityAddReferentFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityAddReferentFailed),
                tap(() =>
                {
                    this.notificationService.notify(
                        [
                            'BACK.ENTITIES.REFERENT.ADD.TITLE',
                            'BACK.ENTITIES.REFERENT.ADD.FAILED'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityRemoveReferentFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveReferentFailed),
                tap(() =>
                {
                    this.notificationService.notify(
                        [
                            'BACK.ENTITIES.REFERENT.REMOVE.TITLE',
                            'BACK.ENTITIES.REFERENT.REMOVE.FAILED'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityRemoveHabilitationRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveHabilitationRequested),
                switchMap(({ entityId, appRole }) =>
                    this.entitiesService.removeHabilitations(entityId, [appRole])
                        .pipe(
                            switchMap(() =>
                            {
                                const entity: Entity = new Entity();
                                const updateEntity: Update<Entity> = {
                                    id: entity.id,
                                    changes: {
                                        applications: entity.applications
                                    }
                                };

                                return [
                                    fromEntitiesGlobalStoreActions.BackAuthEntityRemoveHabilitationSucceeded({ appRole: appRole }),
                                    entity.external ? BackAuthEntityExternalUpdated({ entity: updateEntity }) : BackAuthEntityInternalUpdated({ entity: updateEntity })
                                ];
                            }),
                            catchError(() =>
                                of(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveHabilitationFailed)
                            )
                        )
                )
            )
    );

    backAuthEntityRemoveHabilitationSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveHabilitationSucceeded),
                tap(({ appRole }) =>
                {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.ENTITIES.ROLES.SUCCESS.REMOVED'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityRemoveHabilitationFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityRemoveHabilitationFailed),
                tap(({ appRole }) =>
                {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.ENTITIES.ROLES.FAILED.REMOVED'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntityUpdateBackupQuerDefinitionRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateBackupDefinitionRequested),
                switchMap(({ entityId, BackupDefinitions }: { entityId: string, BackupDefinitions: EntityMaster[]}) =>
                    this.queryDefinitionsService.updateBackupDefinitions(entityId, BackupDefinitions)
                        .pipe(
                            map((entityBackupDefinition) =>
                                fromEntitiesGlobalStoreActions.BackAuthEntityUpdateBackupDefinitionSucceeded(entityBackupDefinition)
                            ),
                            catchError((error) =>
                                of(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateBackupDefinitionFailed(error))
                            )
                        )
                )
            )
    );

    backAuthEntityUpdateBackupDefinitionSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateBackupDefinitionSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                '',
                                'Sauvegardes paramétrées'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityUpdateBackupDefinitionFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateBackupDefinitionFailed),
                    tap((error) =>
                        this.notificationService.notify(
                            [
                                '',
                                error.message
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityUpdateVmwareDefinitionRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVmwareDefinitionRequested),
                switchMap(({ entityId, vmwareFilters }) =>
                    this.queryDefinitionsService.updateVmwareDefinition(entityId, vmwareFilters)
                        .pipe(
                            map((entityComputeDefinition) =>
                                fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVmwareDefinitionSucceeded(entityComputeDefinition)
                            ),
                            catchError((error) =>
                                of(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVmwareDefinitionFailed(error))
                            )
                        )
                )
            )
    );

    backAuthEntityUpdateVmwareDefinitionSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVmwareDefinitionSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                '',
                                'Sauvegardes paramétrées'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityUpdateVmwareDefinitionFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVmwareDefinitionFailed),
                    tap((error) =>
                        this.notificationService.notify(
                            [
                                '',
                                error.message
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityUpdateVeeamDefinitionRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVeeamDefinitionRequested),
                switchMap(({ entityId, veeamFilters }) =>
                    this.queryDefinitionsService.updateVeeamDefinition(entityId, veeamFilters)
                        .pipe(
                            map((entityComputeDefinition: EntityComputeDefinition) =>
                                fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVeeamDefinitionSucceeded(entityComputeDefinition)
                            ),
                            catchError((error) =>
                                of(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVeeamDefinitionFailed(error))
                            )
                        )
                )
            )
    );

    backAuthEntityUpdateVeeamDefinitionSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVeeamDefinitionSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                '',
                                'Sauvegardes paramétrées'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityUpdateVeeamDefinitionFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUpdateVeeamDefinitionFailed),
                    tap((error) =>
                        this.notificationService.notify(
                            [
                                '',
                                error.message
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityUsersUpdateQueryStringRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromEntitiesGlobalStoreActions.BackAuthEntityUsersUpdateQueryStringRequested),
                    withLatestFrom(this.store.select(getSelectedEntity)),
                    tap(([_, entity]) =>
                        this.store.dispatch(BackAuthEntityUsersRequested({ entityId: entity.id }))
                    )
                ),
        { dispatch : false }
    );
}
